import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Truck, Save, Trash2, AlertTriangle, CheckCircle2, Zap } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import Toggle from "@/components/app/Toggle";
import { Field, isBlank, isEmail } from "@/components/app/FormKit";
import { btnOutline, btnPrimary, cardCls, fmtDateTime, inr } from "@/components/app/ui";

const SOURCE_TEXT = {
  database: "Using the Shiprocket account saved here. Delivery charges are calculated live at checkout.",
  environment: "Using the Shiprocket account from the server environment (SHIPROCKET_EMAIL / SHIPROCKET_PASSWORD).",
  none: "No Shiprocket account is set, so checkout uses the standard flat delivery charge.",
};

export default function ShippingSettingsCard() {
  const [cfg, setCfg] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [verify, setVerify] = useState(true);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [test, setTest] = useState({ pickup: "226001", delivery: "110001", weight: "0.5", cod: false });
  const [result, setResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/settings/shipping");
    setCfg(data); setEmail(data.email || ""); setEnabled(data.email ? data.enabled : true);
  }, []);
  useEffect(() => { load().catch(() => toast.error("Could not load shipping settings.")); }, [load]);

  const save = async () => {
    const e = {};
    if (isBlank(email)) e.email = "Enter the Shiprocket account email."; else if (!isEmail(email)) e.email = "Enter a valid email address.";
    if (isBlank(password) && (!cfg.password_set || email.trim() !== cfg.email)) e.password = "Enter the Shiprocket password.";
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings/shipping", { email: email.trim(), password: password || null, enabled, verify });
      setCfg(data); setPassword("");
      toast.success(data.enabled ? "Shiprocket saved. Checkout now quotes live delivery charges." : "Saved, but live delivery charges are switched off.");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove the saved Shiprocket account? Checkout falls back to the standard flat delivery charge.")) return;
    try { const { data } = await api.delete("/admin/settings/shipping"); setCfg(data); setEnabled(true); setResult(null); toast.success("Saved account removed."); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const runTest = async () => {
    setTesting(true); setResult(null);
    try { const { data } = await api.post("/admin/settings/shipping/test", { pickup: test.pickup.trim(), delivery: test.delivery.trim(), weight: parseFloat(test.weight) || 0.5, cod: test.cod }); setResult(data); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  if (!cfg) return null;
  const none = cfg.active_source === "none";
  return (
    <section className={`${cardCls} p-5`} data-testid="shipping-settings">
      <div className="flex items-center gap-2 mb-1"><Truck size={16} className="text-blue-600" /><h2 className="text-[14px] font-bold">Shipping — Shiprocket</h2>
        {cfg.password_set && <span className="ml-auto flex items-center gap-2 text-[12px] text-slate-600">{enabled ? "Live rates" : "Off"}<Toggle checked={enabled} onChange={setEnabled} label="Live delivery charges" t="shipping-enabled" /></span>}</div>
      <p className="text-[12px] text-slate-500 mb-3">Checkout asks Shiprocket for the courier rates from the store&apos;s pickup pincode to the customer&apos;s pincode (using the cart weight and COD/prepaid) and charges the cheapest courier. The access token is renewed automatically when it expires.</p>
      <div className={`rounded-lg border px-3 py-2 flex items-start gap-2 mb-3 text-[12px] ${none ? "bg-orange-50 border-orange-200 text-orange-800" : "bg-green-50 border-green-200 text-green-800"}`} data-testid="shipping-status">
        {none ? <AlertTriangle size={15} className="mt-0.5" /> : <CheckCircle2 size={15} className="mt-0.5" />}<span>{SOURCE_TEXT[cfg.active_source]}</span>
      </div>
      <div className="grid grid-cols-6 gap-x-3 gap-y-3">
        <Field className="col-span-6 sm:col-span-3" label="Shiprocket email" type="email" t="shipping-email" value={email} onChange={setEmail} error={errors.email} autoComplete="off" />
        <Field className="col-span-6 sm:col-span-3" label="Password" type="password" t="shipping-password" value={password} onChange={setPassword} error={errors.password} autoComplete="new-password"
          placeholder={cfg.password_set ? "•••••••• (saved — leave blank to keep)" : "Shiprocket password"} hint="Stored encrypted; never shown again." />
      </div>
      <label className="mt-3 flex items-center gap-2 text-[12px] text-slate-600">
        <input type="checkbox" checked={verify} onChange={(e) => setVerify(e.target.checked)} data-testid="shipping-verify" className="rounded border-slate-300" />Log in to Shiprocket to check the credentials before saving
      </label>
      <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
        <div className="text-[11px] text-slate-500">{cfg.updated_at ? `Last updated ${fmtDateTime(cfg.updated_at)}` : "No account saved yet."}</div>
        <div className="flex gap-2">
          {cfg.password_set && <button onClick={remove} data-testid="shipping-remove" className={btnOutline}><Trash2 size={14} /> Remove account</button>}
          <button onClick={save} disabled={saving} data-testid="shipping-save" className={btnPrimary}><Save size={14} /> {saving ? "Saving…" : "Save account"}</button>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100">
        <div className="text-[12px] font-semibold text-slate-700 mb-2">Try a live quote</div>
        <div className="flex items-end gap-2 flex-wrap">
          <Field className="w-28" label="Pickup pincode" t="shipping-test-pickup" value={test.pickup} onChange={(v) => setTest((t) => ({ ...t, pickup: v.replace(/\D/g, "").slice(0, 6) }))} />
          <Field className="w-28" label="Delivery pincode" t="shipping-test-delivery" value={test.delivery} onChange={(v) => setTest((t) => ({ ...t, delivery: v.replace(/\D/g, "").slice(0, 6) }))} />
          <Field className="w-24" label="Weight (kg)" t="shipping-test-weight" value={test.weight} onChange={(v) => setTest((t) => ({ ...t, weight: v }))} />
          <label className="flex items-center gap-1.5 text-[12px] text-slate-600 h-9"><input type="checkbox" checked={test.cod} onChange={(e) => setTest((t) => ({ ...t, cod: e.target.checked }))} className="rounded border-slate-300" />COD</label>
          <button onClick={runTest} disabled={testing || none} data-testid="shipping-test" className={btnOutline}><Zap size={14} /> {testing ? "Checking…" : "Get quote"}</button>
        </div>
        {result && <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-[13px]" data-testid="shipping-test-result">Best courier: <b>{result.courier}</b> · charge <b>{inr(result.charge)}</b>{result.etd ? ` · delivery by ${result.etd}` : ""}</div>}
      </div>
    </section>
  );
}
